import { useState, useRef, useCallback, useMemo } from 'react'
import { formatDistanceToNow, format } from 'date-fns'
import { th } from 'date-fns/locale'
import { useDebounceValue } from 'usehooks-ts'
import {
	UserX,
	User,
	Calendar,
	MessageSquare,
	ExternalLink,
	Search,
	Loader2,
} from 'lucide-react'

import PageContainer from '@/common/components/PageContainer'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/common/ui/card'
import { Button } from '@/common/ui/button'
import { Input } from '@/common/ui/input'
import { Badge } from '@/common/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/common/ui/avatar'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/common/ui/table'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/common/ui/tooltip'

import { useQueryClosedRooms } from '../../api/hooks'

import type { components } from '@/schema'

type ChatRoomResponse = components['schemas']['ChatRoomResponse']

export function ClosedChatsPage() {
	const [search, setSearch] = useState('')
	const [debouncedSearch] = useDebounceValue(search, 500)
	const tableContainerRef = useRef<HTMLDivElement>(null)

	const {
		data,
		isLoading,
		isError,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useQueryClosedRooms({ search: debouncedSearch || undefined })

	// Flatten all pages into a single array
	const rooms = useMemo(
		() => data?.pages.flatMap((page) => page.items) ?? [],
		[data],
	)

	// Handle scroll for infinite loading
	const handleScroll = useCallback(() => {
		const container = tableContainerRef.current
		if (!container) return

		const { scrollTop, scrollHeight, clientHeight } = container
		const isNearBottom = scrollHeight - scrollTop - clientHeight < 200

		if (isNearBottom && hasNextPage && !isFetchingNextPage) {
			fetchNextPage()
		}
	}, [fetchNextPage, hasNextPage, isFetchingNextPage])

	// Helper to render table content based on state
	const renderTableContent = () => {
		if (isLoading) {
			return (
				<div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
					<Loader2 className="h-5 w-5 animate-spin" />
					กำลังโหลดข้อมูล...
				</div>
			)
		}

		if (isError) {
			return (
				<div className="py-12 text-center text-muted-foreground">
					เกิดข้อผิดพลาดในการโหลดข้อมูล
				</div>
			)
		}

		if (rooms.length === 0) {
			return (
				<div className="py-12 text-center">
					<UserX className="mx-auto mb-4 h-12 w-12 text-muted-foreground/50" />
					<p className="text-muted-foreground">
						{search ? 'ไม่พบห้องแชทที่ตรงกับการค้นหา' : 'ไม่มีห้องแชทที่ถูกปิด'}
					</p>
				</div>
			)
		}

		return (
			<div className="rounded-md border">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead className="w-[250px]">ผู้ใช้</TableHead>
							<TableHead>ผู้เล่นที่เชื่อมต่อ</TableHead>
							<TableHead>ข้อความล่าสุด</TableHead>
							<TableHead>วันที่ปิด</TableHead>
							<TableHead className="text-right">การดำเนินการ</TableHead>
						</TableRow>
					</TableHeader>
				</Table>
				<div
					ref={tableContainerRef}
					onScroll={handleScroll}
					className="max-h-[calc(100vh-380px)] min-h-[300px] overflow-y-auto"
				>
					<Table>
						<TableBody>
							{rooms.map((room) => (
								<ClosedRoomRow key={room.id} room={room} />
							))}
						</TableBody>
					</Table>
				</div>
				{/* Loading indicator for infinite scroll */}
				{isFetchingNextPage && (
					<div className="flex items-center justify-center gap-2 border-t py-4 text-sm text-muted-foreground">
						<Loader2 className="h-4 w-4 animate-spin" />
						กำลังโหลดเพิ่มเติม...
					</div>
				)}
				{/* End of list indicator */}
				{!hasNextPage && rooms.length > 0 && (
					<div className="border-t py-3 text-center text-xs text-muted-foreground">
						แสดงทั้งหมด {rooms.length} รายการ
					</div>
				)}
			</div>
		)
	}

	return (
		<PageContainer>
			<Card>
				<CardHeader>
					<div className="flex items-center justify-between">
						<div>
							<CardTitle className="flex items-center gap-2">
								<UserX className="h-5 w-5 text-muted-foreground" />
								รายการห้องที่ถูกปิด
							</CardTitle>
							<CardDescription>
								ห้องแชทที่ถูกปิดเนื่องจากผู้ใช้ยกเลิกการติดตาม (Unfollow)
							</CardDescription>
						</div>
						{!isLoading && (
							<Badge variant="secondary" className="text-lg">
								{rooms.length} ห้อง
							</Badge>
						)}
					</div>
				</CardHeader>
				<CardContent>
					{/* Search */}
					<div className="relative mb-4">
						<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							placeholder="ค้นหาชื่อ, หรือเบอร์โทร..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="pl-9"
						/>
					</div>

					{renderTableContent()}
				</CardContent>
			</Card>
		</PageContainer>
	)
}

interface ClosedRoomRowProps {
	room: ChatRoomResponse
}

function ClosedRoomRow({ room }: ClosedRoomRowProps) {
	const displayName = room.lineDisplayName || room.lineUserId
	const initials = displayName?.slice(0, 2).toUpperCase() || ''

	return (
		<TableRow>
			<TableCell>
				<div className="flex items-center gap-3">
					<Avatar className="h-10 w-10">
						<AvatarImage src={room.linePictureUrl} alt={displayName} />
						<AvatarFallback>{initials}</AvatarFallback>
					</Avatar>
					<div>
						<div className="font-medium">{displayName}</div>
						<div className="text-xs text-muted-foreground">
							{room.lineUserId}
						</div>
					</div>
				</div>
			</TableCell>
			<TableCell>
				{room.player ? (
					<TooltipProvider>
						<Tooltip>
							<TooltipTrigger asChild>
								<a
									href={`/operator/get-player-list/player-detail/${room.player.id}`}
									target="_blank"
									rel="noopener noreferrer"
									className="flex items-center gap-2 text-primary hover:underline"
								>
									<User className="h-4 w-4" />
									{room.player.phone}
									<ExternalLink className="h-3 w-3" />
								</a>
							</TooltipTrigger>
							<TooltipContent>ดูข้อมูลผู้เล่น</TooltipContent>
						</Tooltip>
					</TooltipProvider>
				) : (
					<span className="text-muted-foreground">-</span>
				)}
			</TableCell>
			<TableCell>
				{room.lastMessage ? (
					<div className="max-w-[200px]">
						<p className="truncate text-sm">{room.lastMessage.text}</p>
						<p className="text-xs text-muted-foreground">
							{formatDistanceToNow(new Date(room.lastMessage.createdAt), {
								addSuffix: true,
								locale: th,
							})}
						</p>
					</div>
				) : (
					<span className="text-muted-foreground">-</span>
				)}
			</TableCell>
			<TableCell>
				{room.lastMessageAt ? (
					<TooltipProvider>
						<Tooltip>
							<TooltipTrigger asChild>
								<div className="flex items-center gap-2 text-sm">
									<Calendar className="h-4 w-4 text-muted-foreground" />
									<span>
										{formatDistanceToNow(new Date(room.lastMessageAt), {
											addSuffix: true,
											locale: th,
										})}
									</span>
								</div>
							</TooltipTrigger>
							<TooltipContent>
								{format(new Date(room.lastMessageAt), 'PPpp', { locale: th })}
							</TooltipContent>
						</Tooltip>
					</TooltipProvider>
				) : (
					<span className="text-muted-foreground">-</span>
				)}
			</TableCell>
			<TableCell className="text-right">
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger asChild>
							<Button variant="ghost" size="icon" asChild>
								<a
									href={`/operator/chat/${room.id}`}
									target="_blank"
									rel="noreferrer"
								>
									<MessageSquare className="h-4 w-4" />
								</a>
							</Button>
						</TooltipTrigger>
						<TooltipContent>ดูห้องแชท</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			</TableCell>
		</TableRow>
	)
}
